import { ListenSVG, Icon, Toast, Button } from "@newsela/angelou";
import {
  withTrackEvent,
  objectTypes,
  actionPrefixes,
  actionNames,
} from "@newsela/snitch";

import useChecksForUnderstandingSubmit from "@/assignments/useChecksForUnderstandingSubmit";
import globalToasterRef from "@/base/globalToasterRef";
import { ICheckForUnderstandingQuizAnswerOption } from "@/types/models/CheckForUnderstandingQuiz.interface";
import useCurrentArticleLevelId from "@/view/utils/useCurrentArticleLevelId";
import { submitButtonCss, toastCss, iconCss } from "./SubmitButton.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

export const SubmitButton = ({
  currentFontSize,
  hasReadAloudInSession,
  isCorrectAnswerVisible,
  isTeacher,
  isListenModeActive,
  isStudentPreviewOn,
  selectedAnswer,
  selectedAnswers,
  setIsReadAloudModeActive,
  setSubmittedAnswers,
  submittedAnswers,
  quizSubmissionPayload,
}: {
  currentFontSize: string;
  hasReadAloudInSession: boolean;
  setIsReadAloudModeActive: (isActive: boolean) => void;
  selectedAnswers: ICheckForUnderstandingQuizAnswerOption[];
  selectedAnswer: ICheckForUnderstandingQuizAnswerOption | undefined;
  setSubmittedAnswers: (
    answers: ICheckForUnderstandingQuizAnswerOption[],
  ) => void;
  submittedAnswers: ICheckForUnderstandingQuizAnswerOption[];
  quizSubmissionPayload: {
    quiz_id: string;
    assignment_content_id?: string | undefined;
    article_content_id: string;
    quiz_question_id: string;
  };
  isListenModeActive: boolean;
  isStudentPreviewOn: boolean;
  isTeacher: boolean;
  isCorrectAnswerVisible: boolean;
}) => {
  const articleLevelId = useCurrentArticleLevelId();

  const [postQuizAnswer, { loading: isSubmittingAnswer }] =
    useChecksForUnderstandingSubmit();

  const isAlreadySubmitted: boolean = submittedAnswers.some(
    (answer: ICheckForUnderstandingQuizAnswerOption) =>
      answer.id === selectedAnswer?.id,
  );

  const isCurrentAnswerCorrect = Boolean(selectedAnswer?.is_correct);

  const shouldPromptToResumeAudio =
    isCurrentAnswerCorrect && hasReadAloudInSession && !isListenModeActive;

  function onToastClick() {
    setIsReadAloudModeActive(shouldPromptToResumeAudio);
  }

  const onSubmit = async () => {
    if (isTeacher) {
      if (isStudentPreviewOn) {
        setSubmittedAnswers(selectedAnswers);
      }
      // early return to skip POST API call for teachers
      return;
    }

    const choiceId = selectedAnswer?.id;

    const payload = {
      ...quizSubmissionPayload,
      article_level_id: articleLevelId,
      choice_id: choiceId,
      is_correct: isCurrentAnswerCorrect,
    };

    if (shouldPromptToResumeAudio) {
      globalToasterRef.current.displayToast({
        cta: {
          onClick: onToastClick,
          text: "Continue listening.",
        },
        children: (
          <>
            <Icon
              SvgComponent={ListenSVG}
              __cssFor={{ root: iconCss }}
              size="MD"
            />
            Ready to continue listening to the article?
          </>
        ),
        dataProps: {
          "data-qa-selector": "read-aloud-keep-listening-article",
        },
        statusColor: Toast.statusColor.primary,
        __cssFor: toastCss,
      });
    }
    await postQuizAnswer(payload);
    setSubmittedAnswers(selectedAnswers);
  };

  const disabled = isTeacher
    ? !isStudentPreviewOn
    : isAlreadySubmitted ||
      !selectedAnswer?.id ||
      !quizSubmissionPayload?.assignment_content_id ||
      isSubmittingAnswer;

  if (isCorrectAnswerVisible) return <></>;
  return (
    <ButtonWithTrackEvent
      actionPrefix={actionPrefixes.initiate}
      actionName={actionNames.update}
      dataProps={{
        "data-qa-selector": "checks_for_understanding_quiz_submit_button",
      }}
      disabled={disabled}
      flavor={Button.flavors.primary}
      isFullWidth
      objectType={objectTypes.quizAnswer}
      onClick={() => {
        void onSubmit();
      }}
      streamProperties={{
        selected_option: isCurrentAnswerCorrect.toString() || "",
      }}
      type="submit"
      __cssFor={{
        root: submitButtonCss(currentFontSize),
      }}
    >
      Submit
    </ButtonWithTrackEvent>
  );
};
